import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Pencil, Trash2, Eye, Snowflake, Flame, ArrowUp, ArrowDown, Menu, LayoutGrid, Rows3 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { deleteLeadWithCalendarCleanup, DeletedLeadData } from "@/lib/deleteLeadWithCalendarCleanup";
import { toast } from "sonner";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import LeadDetailsDialog from "./LeadDetailsDialog";
import { EditableCell } from "./EditableCell";
import ProductsColumn from "./ProductsColumn";
import { ColumnConfig } from "@/lib/columnConfig";
import { getCurrentUser } from "@/lib/authUser";

interface Lead {
  id: string;
  lead_name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  priority: string;
  value: number | null;
  source: string | null;
  conversation_notes: string | null;
  next_action: string | null;
  next_action_date: string | null;
  created_at: string;
  updated_at?: string;
  custom_fields?: Record<string, any>;
  last_refreshed_at?: string | null;
  user_id?: string;
  group_id?: string | null;
}

interface LeadsTableProps {
  leads: Lead[];
  onLeadsChange: () => void;
  columnConfig: ColumnConfig[];
  reminderDays?: number;
  showReminderColumn?: boolean;
  sheetType?: 'fresh' | 'cold';
  onBeforeDelete?: (lead: Lead, deletedData?: DeletedLeadData) => void;
  onBeforeUpdate?: (lead: Lead) => void;
  selectedLeads?: string[];
  onSelectedLeadsChange?: (ids: string[]) => void;
  customActions?: (lead: Lead) => React.ReactNode;
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc';
  externalOpenLeadId?: string | null;
  onExternalOpenLeadClose?: () => void;
  highlightedLeadId?: string | null;
}

const LeadsTable = ({ leads, onLeadsChange, columnConfig, reminderDays = 30, showReminderColumn = true, sheetType, onBeforeDelete, onBeforeUpdate, selectedLeads = [], onSelectedLeadsChange, customActions, sortField = null, sortDirection = 'asc', externalOpenLeadId, onExternalOpenLeadClose, highlightedLeadId }: LeadsTableProps) => {
  
  const { workspace, selectedGroupId } = useWorkspace();
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState<'cards' | 'table'>(() => {
    if (typeof window === 'undefined') return 'cards';
    return (localStorage.getItem('mobileLeadsView') as 'cards' | 'table') || 'cards';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('mobileLeadsView', mobileView);
    }
  }, [mobileView]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [, setCurrentTime] = useState(Date.now());
  const [workspaceProducts, setWorkspaceProducts] = useState<{ id: string; name: string }[]>([]);
  // Batched product associations for all visible leads (single IN(...) query).
  const [leadProductMap, setLeadProductMap] = useState<Record<string, string[]>>({});

  // Fetch products for the current workspace
  useEffect(() => {
    const fetchProducts = async () => {
      const user = await getCurrentUser();
      if (!user) return;

      let query = supabase.from("products").select("id, name").order("name");

      if (workspace === "groups" && selectedGroupId) {
        query = query.eq("group_id", selectedGroupId);
      } else {
        query = query.eq("user_id", user.id).is("group_id", null);
      }

      const { data } = await query;
      setWorkspaceProducts(data || []);
    };
    fetchProducts();
  }, [workspace, selectedGroupId]);

  // Batch-fetch lead_products for all visible leads at once. Replaces the
  // per-row fetch that ProductsColumn was doing (N queries → 1 query).
  useEffect(() => {
    const ids = leads.map((l) => l.id);
    if (ids.length === 0) {
      setLeadProductMap({});
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("lead_products")
        .select("lead_id, product_id")
        .in("lead_id", ids);
      if (cancelled || error) return;
      const map: Record<string, string[]> = {};
      (data || []).forEach((row: any) => {
        (map[row.lead_id] ||= []).push(row.product_id);
      });
      setLeadProductMap(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [leads]);

  // Handle external open lead request (from AI assistant)
  useEffect(() => {
    if (externalOpenLeadId) {
      const leadToOpen = leads.find(l => l.id === externalOpenLeadId);
      if (leadToOpen) {
        setSelectedLead(leadToOpen);
        setDetailsDialogOpen(true);
      }
    }
  }, [externalOpenLeadId, leads]);

  // Update timer every minute to keep days remaining current
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: "bg-blue-500/10 text-blue-500",
      contacted: "bg-yellow-500/10 text-yellow-500",
      qualified: "bg-purple-500/10 text-purple-500",
      proposal: "bg-orange-500/10 text-orange-500",
      negotiation: "bg-cyan-500/10 text-cyan-500",
      won: "bg-green-500/10 text-green-500",
      lost: "bg-red-500/10 text-red-500",
      do_not_contact: "bg-rose-500/10 text-rose-500 line-through",
    };
    return colors[status] || "bg-gray-500/10 text-gray-500";
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: "bg-gray-500/10 text-gray-500",
      medium: "bg-yellow-500/10 text-yellow-500",
      high: "bg-red-500/10 text-red-500",
    };
    return colors[priority] || "bg-gray-500/10 text-gray-500";
  };

  const handleDelete = async () => {
    if (!selectedLead) return;

    try {
      const deletedData = await deleteLeadWithCalendarCleanup(selectedLead.id, {
        groupId: selectedGroupId || null,
        workspaceName: workspace === 'groups' ? undefined : 'Personal',
      });

      // Store lead data + gantt items for undo functionality
      if (onBeforeDelete) {
        onBeforeDelete(selectedLead, deletedData);
      }

      toast.success("Lead deleted", { description: "The lead has been removed successfully." });
      onLeadsChange();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedLead(null);
    }
  };

  const openDeleteDialog = (lead: Lead) => {
    setSelectedLead(lead);
    setDeleteDialogOpen(true);
  };

  const openDetailsDialog = (lead: Lead) => {
    setSelectedLead(lead);
    setDetailsDialogOpen(true);
  };

  const handleCellUpdate = async (leadId: string, field: string, value: string) => {
    try {
      console.log(`Updating lead ${leadId}, field: ${field}, value: ${value}`);
      
      // Store previous state before update
      const currentLead = leads.find(l => l.id === leadId);
      if (currentLead && onBeforeUpdate) {
        onBeforeUpdate(currentLead);
      }

      const updateData: any = { [field]: value };
      
      const { error, data } = await supabase
        .from("leads")
        .update(updateData)
        .eq("id", leadId)
        .select();

      if (error) {
        console.error(`Error updating lead:`, error);
        throw error;
      }

      console.log(`Lead updated successfully:`, data);

      toast.success("Lead updated", { description: `${field.charAt(0).toUpperCase() + field.slice(1)} updated successfully.` });
      
      onLeadsChange();

      // If lead_name changed, re-sync all gantt items to update Google Calendar event titles
      if (field === 'lead_name' && value) {
        try {
          const { data: ganttItems } = await supabase
            .from('lead_gantt_items')
            .select('*')
            .eq('lead_id', leadId);
          
          if (ganttItems && ganttItems.length > 0) {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
              const itemsArray = ganttItems.map(item => ({
                item,
                leadName: value,
                leadId,
                startDate: item.start_date,
                endDate: item.end_date,
                workspaceType: workspace,
                groupId: selectedGroupId,
              }));
              await supabase.functions.invoke('google-calendar-sync/sync-item', {
                method: 'POST',
                headers: { Authorization: `Bearer ${session.access_token}` },
                body: { items: itemsArray, timezone },
              });
            }
          }
        } catch (calSyncErr) {
          console.log('Calendar re-sync after name change failed (non-critical):', calSyncErr);
        }
      }

      // Trigger AI learning from this manual edit
      try {
        await supabase.functions.invoke('analyze-lead-correction', {
          body: { leadId }
        });
        console.log('AI learning analysis triggered for lead:', leadId);
      } catch (analysisError) {
        console.log('AI learning analysis failed (non-critical):', analysisError);
      }
    } catch (error: any) {
      console.error(`Failed to update lead:`, error);
      toast.error("Update failed", { description: error.message || "Could not update the lead. Please try again." });
    }
  };

  const handleCustomFieldUpdate = async (leadId: string, fieldName: string, value: string) => {
    try {
      const lead = leads.find(l => l.id === leadId);
      if (!lead) return;

      // Store previous state before update
      if (onBeforeUpdate) {
        onBeforeUpdate(lead);
      }

      const updatedCustomFields = {
        ...(lead.custom_fields || {}),
        [fieldName]: value
      };

      const { error } = await supabase
        .from("leads")
        .update({ custom_fields: updatedCustomFields })
        .eq("id", leadId);

      if (error) throw error;

      toast.success("Lead updated", { description: "The lead has been updated successfully." });
      onLeadsChange();

      // Trigger AI learning from this manual edit
      try {
        await supabase.functions.invoke('analyze-lead-correction', {
          body: { leadId }
        });
        console.log('AI learning analysis triggered for lead:', leadId);
      } catch (analysisError) {
        console.log('AI learning analysis failed (non-critical):', analysisError);
      }
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const calculateDaysRemaining = (lead: Lead) => {
    const lastRefreshedAt = lead.last_refreshed_at;
    // Use custom reminder days if set, otherwise use global default
    const customDays = lead.custom_fields?.custom_reminder_days;
    const daysToUse = customDays || reminderDays;
    
    if (!lastRefreshedAt) return daysToUse;
    const lastRefreshed = new Date(lastRefreshedAt);
    const now = new Date();
    const daysPassed = Math.floor((now.getTime() - lastRefreshed.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, daysToUse - daysPassed);
  };

  const handleReminderDaysUpdate = async (leadId: string, days: string) => {
    try {
      const daysNum = parseInt(days) || 0;
      const lead = leads.find(l => l.id === leadId);
      if (!lead) return;
      
      // Use custom reminder days if set, otherwise use global default
      const customDays = lead.custom_fields?.custom_reminder_days;
      const daysToUse = customDays || reminderDays;
      
      const now = new Date();
      const newLastRefreshed = new Date(now);
      newLastRefreshed.setDate(newLastRefreshed.getDate() - (daysToUse - daysNum));

      const { error } = await supabase
        .from("leads")
        .update({ last_refreshed_at: newLastRefreshed.toISOString() })
        .eq("id", leadId);

      if (error) throw error;

      toast.success("Reminder updated", { description: "The reminder timer has been updated." });
      onLeadsChange();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleMoveToCold = async (leadId: string) => {
    try {
      // Set last_refreshed_at to a very old date to mark as cold via action button
      const oldDate = new Date('1900-01-01').toISOString();
      
      const { error } = await supabase
        .from("leads")
        .update({ 
          last_refreshed_at: oldDate
        })
        .eq("id", leadId);

      if (error) throw error;

      toast.success("Lead moved to Cold", { description: "The lead has been moved to Cold Leads." });
      onLeadsChange();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleMoveToFresh = async (leadId: string) => {
    try {
      // Reset last_refreshed_at to now to mark as fresh
      const { error } = await supabase
        .from("leads")
        .update({ 
          last_refreshed_at: new Date().toISOString()
        })
        .eq("id", leadId);

      if (error) throw error;

      toast.success("Lead moved to Hot", { description: "The lead has been moved to Hot Leads and the reminder timer has been reset." });
      onLeadsChange();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (onSelectedLeadsChange) {
      onSelectedLeadsChange(checked ? leads.map(lead => lead.id) : []);
    }
  };

  const handleSelectLead = (leadId: string, checked: boolean) => {
    if (onSelectedLeadsChange) {
      if (checked) {
        onSelectedLeadsChange([...selectedLeads, leadId]);
      } else {
        onSelectedLeadsChange(selectedLeads.filter(id => id !== leadId));
      }
    }
  };

  const allSelected = leads.length > 0 && selectedLeads.length === leads.length;
  const someSelected = selectedLeads.length > 0 && selectedLeads.length < leads.length;

  const getSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1 inline-block" />
      : <ArrowDown className="h-3 w-3 ml-1 inline-block" />;
  };

  const sortedLeads = [...leads].sort((a, b) => {
    if (!sortField) return 0;

    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'priority':
        const priorityOrder: Record<string, number> = { low: 1, medium: 2, high: 3 };
        aValue = priorityOrder[a.priority] || 0;
        bValue = priorityOrder[b.priority] || 0;
        break;
      case 'created_at':
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
        break;
      case 'updated_at':
        aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });

  if (leads.length === 0) {
    return (
      <div className="text-center py-12 border-2 bg-card">
        <p className="text-muted-foreground mb-4">No leads yet. Add your first lead to get started!</p>
      </div>
    );
  }

  // Compute visible columns from unified config
  const visibleColumns = columnConfig.filter(col => col.visible !== false);

  const renderHeaderCell = (col: ColumnConfig) => {
    const baseClass = "font-mono text-[10px] uppercase tracking-[0.12em]";
    if (col.type === 'default' && col.field === 'lead_name') {
      return <TableHead key={col.id} className={`sticky left-[60px] z-20 w-[150px] min-w-[150px] max-w-[150px] bg-background px-2 ${baseClass}`}>{col.name}</TableHead>;
    }
    if (col.type === 'default' && col.field === 'company') {
      return <TableHead key={col.id} className={`w-[180px] min-w-[180px] px-2 ${baseClass}`}>{col.name}</TableHead>;
    }
    if (col.type === 'default' && col.field === 'priority') {
      return <TableHead key={col.id} className={`px-2 ${baseClass}`}>{col.name}{getSortIcon('priority')}</TableHead>;
    }
    return <TableHead key={col.id} className={`px-2 ${baseClass}`}>{col.name}</TableHead>;
  };

  const renderBodyCell = (col: ColumnConfig, lead: Lead) => {
    if (col.type === 'default') {
      switch (col.field) {
        case 'lead_name':
          return (
            <TableCell key={col.id} className="sticky left-[60px] z-10 w-[150px] min-w-[150px] max-w-[150px] overflow-hidden bg-background px-2 font-medium">
              <div className="truncate">
                <EditableCell
                  value={lead.lead_name}
                  onSave={(value) => handleCellUpdate(lead.id, "lead_name", value)}
                />
              </div>
            </TableCell>
          );
        case 'company':
          return (
            <TableCell key={col.id} className="w-[180px] min-w-[180px] px-2">
              <EditableCell
                value={lead.company}
                onSave={(value) => handleCellUpdate(lead.id, "company", value)}
              />
            </TableCell>
          );
        case 'email':
          return (
            <TableCell key={col.id} className="px-2">
              <EditableCell
                value={lead.email}
                type="email"
                onSave={(value) => handleCellUpdate(lead.id, "email", value)}
              />
            </TableCell>
          );
        case 'phone':
          return (
            <TableCell key={col.id} className="px-2">
              <EditableCell
                value={lead.phone}
                onSave={(value) => handleCellUpdate(lead.id, "phone", value)}
              />
            </TableCell>
          );
        case 'status':
          return (
            <TableCell key={col.id} className="px-2">
              <Select
                value={lead.status}
                onValueChange={(value) => handleCellUpdate(lead.id, "status", value)}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border shadow-lg z-[100]" sideOffset={5}>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                  <SelectItem value="do_not_contact">Do Not Contact</SelectItem>
                </SelectContent>
              </Select>
            </TableCell>
          );
        case 'priority':
          return (
            <TableCell key={col.id} className="px-2">
              <Select
                value={lead.priority}
                onValueChange={(value) => handleCellUpdate(lead.id, "priority", value)}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border shadow-lg z-[100]" sideOffset={5}>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </TableCell>
          );
        case 'products':
          return (
            <TableCell key={col.id} className="px-2">
              <ProductsColumn
                leadId={lead.id}
                products={workspaceProducts}
                initialSelectedIds={leadProductMap[lead.id] || []}
              />
            </TableCell>
          );
        default:
          return <TableCell key={col.id} className="px-2">-</TableCell>;
      }
    }
    // Custom column
    return (
      <TableCell key={col.id} className="px-2">
        <EditableCell
          value={lead.custom_fields?.[col.name]}
          onSave={(value) => handleCustomFieldUpdate(lead.id, col.name, value)}
        />
      </TableCell>
    );
  };

  return (
    <>
      {isMobile && (
        <div className="mb-2 flex items-center justify-end gap-1">
          <div className="inline-flex items-center rounded-md border border-border bg-card p-0.5">
            <button
              type="button"
              onClick={() => setMobileView('cards')}
              aria-label="Card view"
              aria-pressed={mobileView === 'cards'}
              className={`flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors ${mobileView === 'cards' ? 'bg-accent text-foreground' : 'text-muted-foreground'}`}
            >
              <LayoutGrid className="h-3 w-3" />
              Cards
            </button>
            <button
              type="button"
              onClick={() => setMobileView('table')}
              aria-label="Table view"
              aria-pressed={mobileView === 'table'}
              className={`flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors ${mobileView === 'table' ? 'bg-accent text-foreground' : 'text-muted-foreground'}`}
            >
              <Rows3 className="h-3 w-3" />
              Table
            </button>
          </div>
        </div>
      )}
      {isMobile && mobileView === 'cards' ? (
        <div className="space-y-2">
          {sortedLeads.map((lead) => {
            const days = showReminderColumn ? calculateDaysRemaining(lead) : null;
            const isHighlighted = highlightedLeadId === lead.id;
            return (
              <div
                key={lead.id}
                className={`relative rounded-xl border bg-card p-3 transition-colors ${isHighlighted ? 'border-primary ring-1 ring-primary/30' : 'border-border'}`}
              >
                <div className="flex items-start gap-2">
                  {onSelectedLeadsChange && (
                    <Checkbox
                      checked={selectedLeads.includes(lead.id)}
                      onCheckedChange={(checked) => handleSelectLead(lead.id, checked as boolean)}
                      aria-label={`Select ${lead.lead_name}`}
                      className="mt-1 h-4 w-4 shrink-0"
                    />
                  )}
                  <button
                    onClick={() => openDetailsDialog(lead)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <h3 className="truncate font-semibold text-sm text-foreground">
                        {lead.lead_name || 'Untitled lead'}
                      </h3>
                      <Badge variant="outline" className={`shrink-0 px-1.5 py-0 text-[9px] font-mono uppercase tracking-wider ${getPriorityColor(lead.priority)}`}>
                        {lead.priority}
                      </Badge>
                      <Badge variant="outline" className={`shrink-0 px-1.5 py-0 text-[9px] font-mono uppercase tracking-wider ${getStatusColor(lead.status)}`}>
                        {lead.status?.replace(/_/g, " ")}
                      </Badge>
                    </div>
                    {lead.company && (
                      <p className="truncate text-xs text-muted-foreground mt-0.5">{lead.company}</p>
                    )}
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => openDetailsDialog(lead)}
                    aria-label="View details"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>

                {(days != null || lead.next_action || customActions || sheetType) && (
                  <div className="mt-2 flex items-center justify-between gap-2 border-t border-border/60 pt-2">
                    <div className="flex-1 min-w-0 flex items-center gap-2 text-xs">
                      {days != null && (
                        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
                          {days}d left
                        </span>
                      )}
                      {lead.next_action && (
                        <p className="truncate text-muted-foreground">{lead.next_action}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {customActions ? (
                        customActions(lead)
                      ) : (
                        <>
                          {sheetType === 'fresh' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleMoveToCold(lead.id)}
                              aria-label="Move to Cold"
                            >
                              <Snowflake className="h-4 w-4" />
                            </Button>
                          )}
                          {sheetType === 'cold' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleMoveToFresh(lead.id)}
                              aria-label="Move to Hot"
                            >
                              <Flame className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openDeleteDialog(lead)}
                            aria-label="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
      <div className="rounded-lg border border-border overflow-auto h-[calc(100vh-130px)]">
        <Table className="min-w-max text-sm">
          <colgroup>
            <col className="w-[60px] min-w-[60px]" />
            {visibleColumns.some(c => c.field === 'lead_name') && <col className="w-[150px] min-w-[150px]" />}
            {visibleColumns.some(c => c.field === 'company') && <col className="w-[180px] min-w-[180px]" />}
          </colgroup>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b border-border">
              <TableHead className="sticky left-0 z-20 w-[60px] min-w-[60px] bg-background px-1 font-mono text-[10px] uppercase tracking-[0.12em]">
                {onSelectedLeadsChange && (
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                    className={someSelected ? "data-[state=checked]:bg-primary/50" : ""}
                  />
                )}
              </TableHead>
              {visibleColumns.map(col => renderHeaderCell(col))}
              <TableHead className="px-2 font-mono text-[10px] uppercase tracking-[0.12em]">Next Action</TableHead>
              {showReminderColumn && <TableHead className="px-2 font-mono text-[10px] uppercase tracking-[0.12em]">Days Until Reminder</TableHead>}
              {customActions ? (
                <TableHead className="sticky right-0 z-20 min-w-[280px] bg-background px-2 text-center font-mono text-[10px] uppercase tracking-[0.12em]">Refresher Actions</TableHead>
              ) : (
                <TableHead className="text-right px-2 font-mono text-[10px] uppercase tracking-[0.12em]">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedLeads.map((lead) => (
              <TableRow key={lead.id} className={`border-b border-border/40 hover:bg-muted/30 transition-colors ${highlightedLeadId === lead.id ? 'bg-primary/5' : ''}`}>
                <TableCell className="sticky left-0 z-10 w-[60px] min-w-[60px] bg-background px-1">
                  <div className="flex items-center gap-0.5">
                    {onSelectedLeadsChange && (
                      <Checkbox
                        checked={selectedLeads.includes(lead.id)}
                        onCheckedChange={(checked) => handleSelectLead(lead.id, checked as boolean)}
                        aria-label={`Select ${lead.lead_name}`}
                        className="h-4 w-4"
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => openDetailsDialog(lead)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
                {visibleColumns.map(col => renderBodyCell(col, lead))}
                <TableCell className="max-w-[200px] px-2">
                  <EditableCell
                    value={lead.next_action}
                    onSave={(value) => handleCellUpdate(lead.id, "next_action", value)}
                  />
                </TableCell>
                {showReminderColumn && (
                  <TableCell className="px-2">
                    <EditableCell
                      value={calculateDaysRemaining(lead)}
                      type="number"
                      onSave={(value) => handleReminderDaysUpdate(lead.id, value)}
                    />
                  </TableCell>
                )}
                {customActions ? (
                  <TableCell className="text-right sticky right-0 z-10 bg-background px-2">
                    {customActions(lead)}
                  </TableCell>
                ) : (
                  <TableCell className="text-right px-2">
                    <div className="flex justify-end gap-1">
                      {sheetType === 'fresh' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleMoveToCold(lead.id)}
                          title="Move to Cold Leads"
                        >
                          <Snowflake className="h-4 w-4" />
                        </Button>
                      )}
                      {sheetType === 'cold' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleMoveToFresh(lead.id)}
                          title="Move to Hot Leads"
                        >
                          <Flame className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => openDeleteDialog(lead)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this lead. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {selectedLead && (
        <LeadDetailsDialog
          lead={selectedLead}
          columnConfig={columnConfig}
          open={detailsDialogOpen}
          onOpenChange={(open) => {
            setDetailsDialogOpen(open);
            if (!open && onExternalOpenLeadClose) {
              onExternalOpenLeadClose();
            }
          }}
          onLeadUpdated={onLeadsChange}
          isGroupWorkspace={workspace === "groups"}
        />
      )}
    </>
  );
};

export default LeadsTable;
